<template>
<Teleport to="body">
  <div class="fixed inset-0 z-50 flex justify-center bg-black/60">
    <div class="m-5 w-full md:w-8/12 lg:w-4/12 xl:w-3/12" @click.self="closeDialog(false)" >

      <div class="p-0 bg-gray-50 shadow-xl border border-gray-200 rounded-md text-center">
        
        <div class="flex flex-col w-full text-black">
        
          <BlockUI :visible="blocked">

            <div v-if="blocked" class="absolute inset-0 flex items-center justify-center z-50 bg-black/20">
              <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="5" fill="transparent" animationDuration=".5s" />
            </div>

            <div class="flex flex-col w-full rounded-t-md p-3 bg-gradient-to-r from-[#424c42] to-[#647064]">
              <div>
                <font-awesome-icon :icon="['fas', 'fa-lock']" class="w-6 h-6 text-white" />
              </div>
              <div class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis"> 
                {{ $t('titulo.nuevo.bloqueo') }}
              </div>
              <div class="font-semibold text-white text-sm md:text-2xl truncate whitespace-nowrap overflow-hidden text-ellipsis"> 
                {{ fechaBloqueo }}
              </div>
            </div>

            <Form :validation-schema="schema" @invalid-submit="onInvalidSubmit">

              <div class="container p-4 text-left space-y-3">

                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.hora') }}</label>
                    <Field name="cronogramaSeleccionado" v-slot="{ handleChange, handleBlur, value, errors }" v-model="cronogramaSeleccionado">
                      <Select @blur="handleBlur" filter @update:model-value="handleChange" :model-value="value" :placeholder="$t('label.hora')" class="input-base" :options="cronogramasDisponibles" optionLabel="horaDesde" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && value !== undefined, 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="cronogramaSeleccionado" /></span>
                    </Field>
                  </div>
                </div>
                <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
                  <div class="w-full px-2 md:w-1/1">
                    <label class="block mb-1">{{ $t('label.motivo.bloque') }}</label>
                    <Field name="observaciones" v-slot="{ field, errors }" v-model="observaciones">
                      <InputText v-bind="field" type="text" class="input-base" :class="{ 'bg-gray-50': errors.length === 0, 'input-success': errors.length === 0 && field.value !== '', 'input-error': errors.length > 0}" />
                      <span class="text-sm text-red-500 p-error"><ErrorMessage name="observaciones" /></span>
                    </Field>
                  </div>
                </div>                

              </div>

            </Form>

            <div class="p-3 flex flex-col md:flex-row gap-2 justify-end w-full mt-2 bg-gray-100">
              <a @click="closeDialog(false)" class="cursor-pointer px-6 text-sm font-bold text-red-500 bg-transparent py-3 border border-red-500 shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
                <font-awesome-icon :icon="['fas', 'fa-xmark']" /> {{ $t('boton.cancelar') }}
              </a>
              <a @click="aplicarBlouqueo" class="cursor-pointer px-6 text-sm font-bold text-[#424c42] bg-transparent py-3 border border-[#424c42] shadow-md rounded-xl transform transition-transform duration-200 hover:scale-105 active:scale-95">
                <font-awesome-icon :icon="['fas', 'fa-check-double']" /> {{ $t('boton.aceptar') }}
              </a>
            </div>
          </BlockUI>


        </div>
      </div>

    </div>
  </div>
</Teleport>  

</template>

<script lang="ts">
import * as yup from 'yup';

import { defineComponent } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';

import Swal from 'sweetalert2'
import InputText from 'primevue/inputtext';
import BlockUI from 'primevue/blockui';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';

import { ConfiguracionMeeting, Cronograma } from 'qreventos-suite/src/services/suite.interfaces';
import seguridadServices from '@/services/seguridad.services';
import configuracionMeetingService from '@/services/configuracion.merting.services';
import { Acreditado } from 'qreventos-suite/services';
import meetingService from 'qreventos-suite/meeting';

export default defineComponent({
  components: {
    InputText, BlockUI, ProgressSpinner, Select,
    Field, ErrorMessage, Form
  },
  props: {
    closeFunction: {
      type: Function,
      required: false
    },
    fechaBloqueo: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      blocked: false,
      acreditado: undefined as unknown as Acreditado,
      cronogramaSeleccionado: undefined as unknown as Cronograma,
      cronogramasDisponibles: [] as Cronograma[],
      observaciones: '',
    };
  },
  computed: {
    schema() {
      return yup.object({
        horaBloqueo: yup.mixed().required(this.$t('errors.seleccione.hora')),
        observaciones: yup.string().required(this.$t('errors.ingrese.observacion'))
      });
    }
  },
  methods: {
    onInvalidSubmit(values: any) {
      const errores: number = Object.values(values?.errors || {}).flat().length;
      Swal.fire({ title: "Error", html: this.$t("error.revise") + " " + errores, icon: "error"});
    },
    closeDialog(reload: boolean = false) {
      if (this.closeFunction) {
        this.closeFunction(reload);
      }
    },
    clearBloqueo() {
      this.cronogramaSeleccionado = undefined as unknown as Cronograma;
      this.observaciones = '';
    },
    async fillDisponibles() {
      try {
        this.blocked = true;
        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting:ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        this.cronogramasDisponibles = await meetingService.findCitasDisponiblesFechaBy(configuracionMeeting.evento.id, hash, this.fechaBloqueo);
      }
      catch (error) {
        this.blocked = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.blocked = false;
      }
    },
    async aplicarBlouqueo() {
      try {
        if (this.observaciones == "") {
          return;
        }
        this.blocked = true;

        const hash: string = seguridadServices.getLocalHash() as string;
        const configuracionMeeting:ConfiguracionMeeting = configuracionMeetingService.get() as ConfiguracionMeeting;

        await meetingService.bloquear(configuracionMeeting.evento.id, hash, this.cronogramaSeleccionado.id, this.observaciones);
        this.closeDialog(true);
      }
      catch (error) {
        this.blocked = false;
        Swal.fire({ title: "Error", text: 'Error [' + error + ']', icon: "error" });
      } finally {
        this.blocked = false;
      }
    }, 
  },
  async mounted() {
    document.body.style.overflow = "hidden";
    this.clearBloqueo();
    this.fillDisponibles();
  },
  unmounted() {
    document.body.style.overflow = "";
  }  
});
</script>